import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    { title: 'Accuracy.', highlight: 'Accountability.', end: 'Assurance.', subtitle: null },
    { title: 'Simplifying', highlight: 'US Incorporation', end: '', subtitle: 'for US founders' },
    { title: 'Book-keeping', highlight: 'Services', end: '', subtitle: 'that scale with you' }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <section className="relative overflow-hidden">
      {/* Hero Section - Dark Cinematic */}
      <div className="relative min-h-[90vh] flex items-center bg-[#1a1a1a]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center transition-transform duration-1000"
            style={{ 
              backgroundImage: 'url(https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&h=1080&fit=crop)',
              transform: `scale(${1.05 + currentSlide * 0.02})`
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] via-[#1a1a1a]/90 to-[#1a1a1a]/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16 w-full pt-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              {/* Slide Number */}
              <div className="flex items-center space-x-4 mb-8">
                <span className="text-5xl font-bold text-white/20">{String(currentSlide + 1).padStart(2, '0')}</span>
                <div className="w-12 h-[1px] bg-white/30"></div>
                <span className="text-white/50 text-sm tracking-widest uppercase">Our Promise</span>
              </div>

              {/* Main Text */}
              <div className="min-h-[200px]">
                {slides.map((slide, index) => (
                  <div
                    key={index}
                    className={`transition-all duration-700 ${
                      currentSlide === index 
                        ? 'opacity-100 translate-x-0' 
                        : 'opacity-0 -translate-x-8 absolute'
                    }`}
                  >
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
                      {slide.title}{' '}
                      <span className="text-[#8D6E63]">{slide.highlight}</span>{' '}
                      {slide.end}
                    </h1>
                    {slide.subtitle && (
                      <p className="text-2xl md:text-3xl text-white/70 font-light">{slide.subtitle}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <div className="flex items-center space-x-4 mt-12">
                <button 
                  onClick={prevSlide}
                  className="w-14 h-14 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-[#1a1a1a] transition-all"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button 
                  onClick={nextSlide}
                  className="w-14 h-14 rounded-full border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-[#1a1a1a] transition-all"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                <div className="ml-6 flex items-center space-x-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-1 rounded-full transition-all duration-500 ${
                        currentSlide === index ? 'w-8 bg-[#8D6E63]' : 'w-2 bg-white/30 hover:bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-12">
                <a href="mailto:hello@trueledger.com?subject=Inquiry%20from%20Website&body=Hi%20Trueledger%20Team,%0A%0AI%20would%20like%20to%20learn%20more%20about%20your%20services.%0A%0AThank%20you.">
                  <Button className="bg-white text-[#1a1a1a] hover:bg-[#8D6E63] hover:text-white px-10 py-6 text-lg transition-all">
                    Speak with Trueledger
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Side - Stats/Info */}
            <div className="hidden lg:block">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-6">
                  <div className="border-l-2 border-[#8D6E63] pl-4">
                    <p className="text-4xl font-bold text-white">10+</p>
                    <p className="text-white/50 text-sm">Clients</p>
                  </div>
                  <div className="border-l-2 border-[#8D6E63] pl-4">
                    <p className="text-4xl font-bold text-white">30+</p>
                    <p className="text-white/50 text-sm">Years of Expertise</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Cards Section */}
      <div className="bg-white py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ServiceCard
              title="Book-keeping Services"
              description="Comprehensive book-keeping solutions for US startups and SMBs with accurate financial records."
              link="/services?service=0"
            />
            <ServiceCard
              title="Audit & Assurance Services"
              description="Our services can be benchmarked with the highest degree of Integrity and professional standards."
              link="/services?service=1"
            />
            <ServiceCard
              title="India Entry Strategy"
              description="Structured launchpad for US companies expanding into India with complete regulatory support."
              link="/services?service=2"
            />
            <ServiceCard
              title="Tax Preparation Services"
              description="Expert tax preparation and filing services ensuring compliance with all US tax regulations."
              link="/services?service=3"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ title, description, link }) => {
  const [isInView, setIsInView] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // On mobile, trigger color when card is 60% visible
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          setIsInView(true);
        } else {
          setIsInView(false);
        }
      },
      { threshold: [0, 0.6, 1] }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Link to={link} className="block h-full">
      <div 
        ref={cardRef}
        className={`relative aspect-square border border-stone-200 overflow-hidden shadow-sm cursor-pointer h-full transition-colors duration-500 group
          ${isInView ? 'bg-[#3E2723] md:bg-white' : 'bg-white'}
          md:hover:bg-[#3E2723]`}
      >
        <div className="p-6 h-full flex flex-col">
          <div className="flex-grow"></div>
          <h4 className={`font-bold text-lg leading-tight transition-colors duration-500 text-left min-h-[56px] flex items-end
            ${isInView ? 'text-white md:text-[#3E2723]' : 'text-[#3E2723]'}
            md:group-hover:text-white`}>
            {title}
          </h4>
          <p className={`text-sm leading-relaxed mt-3 mb-4 transition-colors duration-500 min-h-[80px] text-left
            ${isInView ? 'text-white md:text-gray-600' : 'text-gray-600'}
            md:group-hover:text-white`}>
            {description}
          </p>
          <div>
            <ChevronRight className={`w-6 h-6 transition-colors duration-500
              ${isInView ? 'text-white md:text-[#3E2723]' : 'text-[#3E2723]'}
              md:group-hover:text-white`} />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default HeroSection;
